/**
 * Subscription Context
 * Provides a centralized SubscriptionClient with retained data support
 * Hooks can subscribe to multi-feed sub-channels without managing WebSocket connections directly
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { SubscriptionClient } from "../sdk";
import { ConnectionState, StreamMessage } from "../sdk/types";
import type { MultiplexStreamSubscription } from "../sdk/types/api";
import { DEFAULT_WS_URL } from "../configs/base";

interface SubscriptionContextType {
  client: SubscriptionClient;
  isEnabled: boolean;
  isConnected: boolean;
  connectionState: ConnectionState;
  error: Error | null;
  toggleEnabled: () => void;
  setEnabled: (enabled: boolean) => void;
  clearError: () => void;
  // Retained data: latest data per sub-channel
  getRetainedData: (subChannel: string) => StreamMessage | null;
  getAllRetainedData: () => Map<string, StreamMessage>;
  clearRetainedData: (subChannel?: string) => void;
  // Subscription methods with retention
  subscribeMultiFeedWithRetention: (
    marketId: string,
    symbol: string,
    callback: (data: StreamMessage) => void,
    interval?: string,
  ) => void;
  subscribeSubChannelWithRetention: (
    marketId: string,
    symbol: string,
    subChannel: string,
    callback: (data: unknown) => void,
    interval?: string,
  ) => void;
  unsubscribeMultiFeed: (
    marketId: string,
    symbol: string,
    callback?: (data: StreamMessage) => void,
  ) => void;
  unsubscribeSubChannel: (
    subChannel: string,
    callback?: (data: unknown) => void,
  ) => void;
  // Configuration
  wsURL: string;
  setWsURL: (url: string) => void;
  authToken: string | undefined;
  setAuthToken: (token: string | undefined) => void;
  walletAddress: string | undefined;
  setWalletAddress: (address: string | undefined) => void;
  deviceID: string | undefined;
  setDeviceID: (deviceId: string | undefined) => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

interface SubscriptionProviderProps {
  children: ReactNode;
  defaultWsURL?: string;
  defaultAuthToken?: string;
  defaultWalletAddress?: string;
  defaultDeviceID?: string;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({
  children,
  defaultWsURL = DEFAULT_WS_URL,
  defaultAuthToken,
  defaultWalletAddress,
  defaultDeviceID,
}) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    ConnectionState.Disconnected,
  );
  const [error, setError] = useState<Error | null>(null);
  const [wsURL, setWsURL] = useState(defaultWsURL);
  const [authToken, setAuthToken] = useState<string | undefined>(
    defaultAuthToken,
  );
  const [walletAddress, setWalletAddress] = useState<string | undefined>(
    defaultWalletAddress,
  );
  const [deviceID, setDeviceID] = useState<string | undefined>(
    defaultDeviceID,
  );

  // Initialize client immediately - it should never be null
  const clientRef = useRef<SubscriptionClient>(
    new SubscriptionClient({
      url: defaultWsURL,
      authToken: defaultAuthToken,
      walletAddress: defaultWalletAddress,
      deviceID: defaultDeviceID,
    })
  );
  const previousWsURLRef = useRef<string>(defaultWsURL);
  const unsubscribeStateChangeRef = useRef<(() => void) | null>(null);
  const retainedDataRef = useRef<Map<string, StreamMessage>>(new Map());
  const wrappedCallbacksRef = useRef<Map<(data: unknown) => void, (data: unknown) => void>>(new Map());
  const multiplexStreamSubsRef = useRef<MultiplexStreamSubscription[]>([]);
  const multiplexUnsubFnsRef = useRef<(() => void)[]>([]);
  const multiFeedCallbackRef = useRef<((data: StreamMessage) => void) | null>(null);
  const subChannelSubsRef = useRef<
    Map<string, { unsubFn: () => void; sub: MultiplexStreamSubscription }>
  >(new Map());

  // Create/recreate subscription client when URL or credentials change
  useEffect(() => {
    // Check if we need to recreate the client (URL or credentials changed)
    const urlChanged = previousWsURLRef.current !== wsURL;
    const needsRecreation = urlChanged;

    if (needsRecreation) {
      // Clean up existing client
      if (unsubscribeStateChangeRef.current) {
        unsubscribeStateChangeRef.current();
        unsubscribeStateChangeRef.current = null;
      }
      clientRef.current.disconnect();

      // Clear retained data when reconnecting to a new URL
      retainedDataRef.current.clear();

      // Create new client with current settings
      const client = new SubscriptionClient({
        url: wsURL,
        authToken,
        walletAddress,
        deviceID,
      });
      clientRef.current = client;
      previousWsURLRef.current = wsURL;
    }

    // Handle connection state based on isEnabled
    if (isEnabled) {
      // Track connection state if not already tracking
      if (!unsubscribeStateChangeRef.current) {
        const unsubscribeStateChange = clientRef.current.onStateChange((state) => {
          setConnectionState(state);
          setIsConnected(state === ConnectionState.Connected);
          // Clear error on successful connection
          if (state === ConnectionState.Connected) {
            setError(null);
          }
        });
        unsubscribeStateChangeRef.current = unsubscribeStateChange;
      }

      // Connect if not already connected
      if (!clientRef.current.isConnected()) {
        clientRef.current
          .connect()
          .then(() => {
            console.debug("[SubscriptionContext] Connected to WebSocket");
            setError(null);
          })
          .catch((err) => {
            const error =
              err instanceof Error ? err : new Error(String(err));
            console.error("[SubscriptionContext] Failed to connect:", error);
            setError(error);
          });
      }
    } else {
      // Disconnect when disabled, but keep client instance
      if (unsubscribeStateChangeRef.current) {
        unsubscribeStateChangeRef.current();
        unsubscribeStateChangeRef.current = null;
      }
      if (clientRef.current.isConnected()) {
        clientRef.current.disconnect();
      }
      // Clear retained data when disabled
      retainedDataRef.current.clear();
      // Clear wrapped callbacks tracking
      wrappedCallbacksRef.current.clear();
      setConnectionState(ConnectionState.Disconnected);
      setIsConnected(false);
      setError(null);
    }

    return () => {
      // Cleanup on unmount or dependency change
      if (unsubscribeStateChangeRef.current) {
        unsubscribeStateChangeRef.current();
        unsubscribeStateChangeRef.current = null;
      }
      // Disconnect but keep client instance (never set to null)
      if (clientRef.current.isConnected()) {
        clientRef.current.disconnect();
      }
    };
  }, [isEnabled, wsURL, authToken, walletAddress, deviceID]);

  // Update client auth when credentials change
  useEffect(() => {
    if (clientRef.current && isEnabled) {
      if (authToken && walletAddress && deviceID) {
        clientRef.current.setAuthToken(authToken, walletAddress, deviceID);
      }
    }
  }, [isEnabled, authToken, walletAddress, deviceID]);

  /**
   * Subscribe to multiplex streams with data retention (replaces legacy multi_feed).
   * Uses multiplex stream types: price_feed, markPrice, orderbook, trades for the given market.
   */
  const subscribeMultiFeedWithRetention = useCallback(
    (
      marketId: string,
      _symbol: string,
      callback: (data: StreamMessage) => void,
      _interval?: string,
    ) => {
      if (!clientRef.current) {
        console.warn("[SubscriptionContext] Cannot subscribe: client not initialized");
        return;
      }
      if (!marketId || typeof marketId !== "string") {
        console.warn("[SubscriptionContext] Invalid marketId:", marketId);
        return;
      }

      multiFeedCallbackRef.current = callback;
      if (multiplexStreamSubsRef.current.length > 0) {
        return;
      }

      const marketIndex = Number(marketId);
      const mi = Number.isNaN(marketIndex) ? 0 : marketIndex;
      const subs: MultiplexStreamSubscription[] = [
        { stream_type: "price_feed", filter: { marketIndices: [mi] } },
        { stream_type: "markPrice", filter: { marketIndices: [mi] } },
        { stream_type: "orderbook", filter: { marketIndices: [mi], includeSnapshot: true } },
        { stream_type: "trades", filter: { marketIndices: [mi] } },
      ];
      multiplexStreamSubsRef.current = subs;

      const multiplex = clientRef.current.getMultiplexStreamClient();
      const unsubFns: (() => void)[] = [];
      const pushMessage = (streamType: string, data: unknown) => {
        const message: StreamMessage = {
          type: "data",
          channel: "multiplex",
          subChannel: streamType,
          data,
        };
        retainedDataRef.current.set(streamType, message);
        multiFeedCallbackRef.current?.(message);
      };

      multiplex
        .subscribeToChannel()
        .then(() => multiplex.subscribeStreams(subs))
        .then(() => {
          unsubFns.push(
            multiplex.onStreamData("price_feed", (r) => {
              if (r.data != null) pushMessage("price_feed", r.data);
            }),
          );
          unsubFns.push(
            multiplex.onStreamData("markPrice", (r) => {
              if (r.data != null) pushMessage("markPrice", r.data);
            }),
          );
          unsubFns.push(
            multiplex.onStreamData("orderbook", (r) => {
              if (r.data != null) pushMessage("orderbook", r.data);
            }),
          );
          unsubFns.push(
            multiplex.onStreamData("trades", (r) => {
              if (r.data != null) pushMessage("trades", r.data);
            }),
          );
          multiplexUnsubFnsRef.current = unsubFns;
        })
        .catch((err) => {
          const error = err instanceof Error ? err : new Error(String(err));
          console.error("[SubscriptionContext] Multiplex subscription error:", error);
          setError(error);
          multiplexStreamSubsRef.current = [];
        });
    },
    [],
  );

  /**
   * Subscribe to a single multiplex stream (sub-channel) with data retention.
   * subChannel must be a valid MultiplexStreamType (e.g. "orderbook", "trades", "markPrice").
   */
  const subscribeSubChannelWithRetention = useCallback(
    (
      marketId: string,
      symbol: string,
      subChannel: string,
      callback: (data: unknown) => void,
      _interval?: string,
    ) => {
      if (!clientRef.current) {
        console.warn("[SubscriptionContext] Cannot subscribe: client not initialized");
        return;
      }
      if (!marketId || typeof marketId !== "string") {
        console.warn("[SubscriptionContext] Invalid marketId:", marketId);
        return;
      }
      if (symbol != null && symbol !== "" && (typeof symbol !== "string" || !symbol.includes("/"))) {
        console.warn("[SubscriptionContext] Invalid symbol (must be empty or BASE/QUOTE):", symbol);
        return;
      }
      if (!subChannel || typeof subChannel !== "string") {
        console.warn("[SubscriptionContext] Invalid subChannel:", subChannel);
        return;
      }
      if (subChannelSubsRef.current.has(subChannel)) {
        return;
      }

      const marketIndex = Number(marketId);
      const mi = Number.isNaN(marketIndex) ? 0 : marketIndex;
      const sub: MultiplexStreamSubscription = {
        stream_type: subChannel as import("../sdk/types/api").MultiplexStreamType,
        filter: subChannel === "orderbook" ? { marketIndices: [mi], includeSnapshot: true } : { marketIndices: [mi] },
      };
      const multiplex = clientRef.current.getMultiplexStreamClient();

      multiplex
        .subscribeToChannel()
        .then(() => multiplex.subscribeStreams([sub]))
        .then(() => {
          const unsubFn = multiplex.onStreamData(
            subChannel as import("../sdk/types/api").MultiplexStreamType,
            (r) => {
              if (r.data == null) return;
              const message: StreamMessage = {
                type: "data",
                channel: "multiplex",
                subChannel,
                data: r.data,
              };
              retainedDataRef.current.set(subChannel, message);
              callback(r.data);
            },
          );
          subChannelSubsRef.current.set(subChannel, { unsubFn, sub });
        })
        .catch((err) => {
          const error = err instanceof Error ? err : new Error(String(err));
          console.error("[SubscriptionContext] Sub-channel subscription error:", error);
          setError(error);
        });
    },
    [],
  );

  /**
   * Unsubscribe from multiplex streams (multi-feed style).
   */
  const unsubscribeMultiFeed = useCallback(
    (_marketId: string, _symbol: string, _callback?: (data: StreamMessage) => void) => {
      if (!clientRef.current) return;
      try {
        multiplexUnsubFnsRef.current.forEach((fn) => fn());
        multiplexUnsubFnsRef.current = [];
        if (multiplexStreamSubsRef.current.length > 0) {
          clientRef.current.getMultiplexStreamClient().unsubscribeStreams(multiplexStreamSubsRef.current);
          multiplexStreamSubsRef.current = [];
        }
        multiFeedCallbackRef.current = null;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        console.error("[SubscriptionContext] Unsubscribe error:", error);
        setError(error);
      }
    },
    [],
  );

  /**
   * Unsubscribe from a specific multiplex sub-channel.
   */
  const unsubscribeSubChannel = useCallback((subChannel: string, _callback?: (data: unknown) => void) => {
    if (!clientRef.current) return;
    const entry = subChannelSubsRef.current.get(subChannel);
    if (!entry) return;
    try {
      entry.unsubFn();
      clientRef.current.getMultiplexStreamClient().unsubscribeStreams([entry.sub]);
      subChannelSubsRef.current.delete(subChannel);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error("[SubscriptionContext] Unsubscribe error:", error);
      setError(error);
    }
  }, []);

  const toggleEnabled = useCallback(() => {
    setIsEnabled((prev) => !prev);
  }, []);

  const setEnabled = useCallback((enabled: boolean) => {
    setIsEnabled(enabled);
  }, []);

  /**
   * Get retained data for a specific sub-channel
   * @param subChannel - Sub-channel name
   * @returns Retained StreamMessage or null if not found
   */
  const getRetainedData = useCallback((subChannel: string) => {
    return retainedDataRef.current.get(subChannel) || null;
  }, []);

  /**
   * Get all retained data
   * @returns A copy of the Map containing all retained data
   */
  const getAllRetainedData = useCallback(() => {
    return new Map(retainedDataRef.current);
  }, []);

  /**
   * Clear retained data for a specific sub-channel or all if no subChannel provided
   * @param subChannel - Optional sub-channel name. If not provided, clears all retained data
   */
  const clearRetainedData = useCallback((subChannel?: string) => {
    if (subChannel) {
      retainedDataRef.current.delete(subChannel);
    } else {
      retainedDataRef.current.clear();
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Expose client with retained data wrappers
  const contextValue = useMemo(
    () => ({
      client: clientRef.current,
      isEnabled,
      isConnected,
      connectionState,
      error,
      toggleEnabled,
      setEnabled,
      clearError,
      getRetainedData,
      getAllRetainedData,
      clearRetainedData,
      wsURL,
      setWsURL,
      authToken,
      setAuthToken,
      walletAddress,
      setWalletAddress,
      deviceID,
      setDeviceID,
      // Expose subscription methods with retention
      subscribeMultiFeedWithRetention,
      subscribeSubChannelWithRetention,
      unsubscribeMultiFeed,
      unsubscribeSubChannel,
    }),
    [
      isEnabled,
      isConnected,
      connectionState,
      error,
      toggleEnabled,
      setEnabled,
      clearError,
      getRetainedData,
      getAllRetainedData,
      clearRetainedData,
      wsURL,
      authToken,
      walletAddress,
      deviceID,
      subscribeMultiFeedWithRetention,
      subscribeSubChannelWithRetention,
      unsubscribeMultiFeed,
      unsubscribeSubChannel,
    ],
  );

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

/**
 * Hook to access subscription context
 * Must be used within SubscriptionProvider
 */
export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error(
      "useSubscription must be used within SubscriptionProvider",
    );
  }
  return context;
};
